import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { authService } from '../service/auth.service';
import { useThrottle } from '../../hooks/useThrottle';

interface IAuthProviderProps {
  children: React.ReactNode;
}

const Context = React.createContext<{
  isAuthenticated: null | boolean;
  isLoading: boolean;
  user: { email: string } | null;
  isAdmin: boolean;
  setIsAuthenticated: (value: null | boolean) => void;
  checkAuth: () => void;
}>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  isAdmin: false,
  setIsAuthenticated: (value: null | boolean) => {},
  checkAuth: () => {}
});

export const AuthProvider = ({ children }: IAuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState<null | boolean>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [user, setUser] = React.useState<{ email: string } | null>(null);
  const [isAdmin, setIsAdmin] = React.useState(false);

  const { mutateAsync: checkMe } = useMutation({
    mutationFn: () => authService.getMe(),
    onSuccess: (result) => {
      if (result?.user?.email) {
        setIsAuthenticated(true);
        setUser({ email: result.user.email });
        setIsAdmin(result.user.isAdmin === true);
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setIsAdmin(false);
      }
      setIsLoading(false);
    },
    onError: () => {
      setIsAuthenticated(false);
      setUser(null);
      setIsAdmin(false);
      setIsLoading(false);
    }
  });
  const throttledCheckMe = useThrottle(checkMe);

  async function checkAuth() {
    setIsLoading(true);
    try {
      await throttledCheckMe();
    } catch {
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }

  React.useEffect(() => {
    checkAuth();
  }, []);

  const setIsAuthenticatedWithUser = React.useCallback((value: null | boolean) => {
    setIsAuthenticated(value);
    if (!value) {
      setUser(null);
      setIsAdmin(false);
    }
  }, []);

  const value = React.useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      user,
      isAdmin,
      setIsAuthenticated: setIsAuthenticatedWithUser,
      checkAuth
    }),
    [isAuthenticated, setIsAuthenticatedWithUser, isLoading, user, isAdmin]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

const useAuth = () => {
  const context = React.useContext(Context);

  if (!context) {
    throw new Error('useAuth() can only be used within AuthProvider');
  }

  return context;
};

export default useAuth;
